/**
 * @fileoverview Classes for handling media stream recording functionality.
 *
 * This module provides specialized recorder classes for capturing audio, video, and screen
 * recordings from various media sources within the browser environment.
 *
 * @module utils/mediaRecorders
 * @author griboni@snowfly.com
 * @version 1.0.0
 */
import debug from "./debug";

/**
 * MIME type used for audio recordings.
 *
 * @type {string}
 */
const AUDIO_CODEC = 'audio/mp4';//'audio/webm;codecs=opus';

/**
 * MIME type used for video/screen recordings.
 *
 * @type {string}
 */
const VIDEO_CODEC = 'video/mp4';//'video/webm;codecs=opus,vp8';

/**
 * The `StreamRecorder` class provides functionality to record media streams
 * and manage the recording process, including starting, stopping, pausing,
 * resuming, and interacting with the underlying stream and file system.
 */
export class StreamRecorder {
    streamType = 'unknown';
    streamExtension = 'stream';
    #stream = null;
    streamOptions = {};
    dataTimer = 1000;
    recorder = null;
    prepared = false;
    fileName = '';
    fileHandle = null;
    fileWriteHandle = null;
    filePos = 0;
    fileClosed = false;
    closed = false;
    locked = false;

    /**
     * Constructs a new instance of the class and initializes default properties.
     * This constructor sets up the initial configuration for a stream handler,
     * including stream type, extension, file handling parameters, and preparation status.
     *
     * @return {void} This constructor does not return a value.
     */
    constructor () {
        this.streamType = 'unknown';
        this.streamExtension = 'stream';
        this.#stream = null;
        this.streamOptions = {};
        this.dataTimer = 1000;
        this.recorder = null;
        this.prepared = false;
        this.fileName = '';
        this.fileHandle = null;
        this.fileWriteHandle = null;
        this.filePos = 0;
        this.fileClosed = false;
    }

    /**
     * Closes the file write handle if it exists and is not already closed.
     * Ensures that all buffered data is flushed before closing.
     * If the write handle's desired size is not empty, the method attempts to close after a delay.
     *
     * @return {Promise<void>} A promise that resolves once the close operation is complete.
     */
    async close() {
        this.locked = true;
        if (this.fileWriteHandle && !this.closed) {
            if (this.fileWriteHandle.getWriter().desiredSize < 1) {
                await this.fileWriteHandle.flush();
                await this.fileWriteHandle.close();
                this.fileWriteHandle = null;
                this.closed = true;
                this.locked = false;
            } else {
                setTimeout(this.close, 100);
            }
        }
    }

    /**
     * Prepares the recorder for usage by initializing necessary file handles
     * and setting up associated resources. Performs required steps to ensure
     * the recorder is ready and prepared for operations.
     *
     * @return {Promise<boolean>} A promise that resolves to `true` when the recorder
     * is successfully prepared.
     */
    async prepare() {
        let self = this;

        return new Promise((resolve/*, reject*/) => {
            if (self.prepared) {
                resolve(true);
                return true;
            }

            /*
                Attempt to obtain a writeable file handle for the stream file.
             */
            self.fileName = `${self.#stream.id}.${self.streamExtension}`;
            navigator.storage.getDirectory().then(async (fsRoot) => {
                /*
                    Create the file in the user's private file system
                 */
                fsRoot.getFileHandle(
                    self.fileName,
                    {create: true}
                ).then((handle) => {
                    return new Promise((innerResolve/*, innerReject*/) => {
                        /*
                         Now that the file was created, attempt to obtain a
                         writeable handle
                         */
                        self.fileHandle = handle;
                        if (handle.createSyncAccessHandle) {
                            handle.createSyncAccessHandle().then((handle2) => {
                                self.fileWriteHandle = handle2;
                                self.fileWriteHandle.truncate(0).then(() => {
                                    innerResolve();
                                });
                            });
                        } else {
                            handle.createWritable(
                                {keepExistingData: true, mode: 'exclusive'}
                            ).then((handle2) => {
                                self.fileWriteHandle = handle2;
                                self.fileWriteHandle.truncate(0).then(() => {
                                    innerResolve();
                                });
                            });
                        }
                    });
                }).then(() => {
                    /*
                        The file handle was acquired, continue with the initialization
                     */
                    if (self.recorder && self.#stream) {
                        if (!self.prepared) {
                            self._initHandlers();
                        }

                        self.prepared = true;
                        resolve(self.prepared);
                    }
                });
            });
        });
    }

    /**
     * Retrieves the current stream instance.
     *
     * @return {Stream} The private stream object associated with this instance.
     */
    get stream() {
        return this.#stream;
    }

    /**
     * Sets the stream used by this recorder.
     *
     * @param newStream
     * @returns {void}
     */
    set stream(newStream) {
        this.#stream = newStream;
    }

    /**
     * Indicates if the stream is ready
     *
     * @returns {null}
     */
    get ready() {
        return this.#stream && this.#stream.active;
    }

    /**
     * Starts the screen recording process by initializing required handlers
     * and dispatching a 'recorder.started' event upon successful start.
     *
     * This method ensures that the recorder is properly prepared before starting.
     * If preparation is incomplete, it initializes the necessary components.
     *
     * @return {Promise<void>} A promise that resolves when the recording process has successfully started.
     */
    async start() {
        if (!this.prepared) {
            await this.prepare();
            await this._initHandlers();
        }
        //debug('<=================> ScreenRecorder ready: %o', this);

        await this.recorder.start(this.dataTimer);

        //debug('Recorder started: %o', this);
        const ev = new CustomEvent('recorder.started', {detail: {streamRecorder: this}});
        document.dispatchEvent(ev);
    }

    /**
     * Stops the recorder if it has been prepared and performs necessary cleanup operations.
     * Dispatches a custom 'recorder.stopped' event upon completion.
     * @return {Promise<void>} Resolves when the recorder has been stopped and cleanup is complete.
     */
    async stop() {
        if (this.prepared) {
            await this.recorder?.stop();
            await this._cleanHandlers();
        }

        //debug('Recorder stopped: %o', this);
        const ev = new CustomEvent('recorder.stopped', {detail: {streamRecorder: this}});
        document.dispatchEvent(ev);
    }

    /**
     * Pauses the recorder and dispatches a custom event to notify that the recorder has been paused.
     *
     * @return {Promise<void>} A promise that resolves once the recorder has been paused and the event has been dispatched.
     */
    async pause() {
        await this.recorder.pause();

        const ev = new CustomEvent('recorder.paused', {detail: {streamRecorder: this}});
        document.dispatchEvent(ev);
    }

    /**
     * Resumes the recording process of the recorder instance.
     * Dispatches a custom event 'recorder.resumed' to notify listeners that the recorder has been resumed.
     *
     * @return {Promise<void>} A promise that resolves once the recorder has been successfully resumed and the event has been dispatched.
     */
    async resume() {
        await this.recorder.resume();

        const ev = new CustomEvent('recorder.resumed', {detail: {streamRecorder: this}});
        document.dispatchEvent(ev);
    }

    /**
     * Initializes the event handlers for the recorder object. If the handlers are already set up,
     * the method ensures they are cleaned up before reinitializing them.
     *
     * The following event listeners are added to the recorder:
     * - `dataavailable`: Triggered when recorded data is available.
     * - `start`: Triggered when recording starts.
     * - `stop`: Triggered when recording stops.
     * - `pause`: Triggered when recording is paused.
     * - `resume`: Triggered when recording resumes.
     * - `error`: Triggered when an error occurs during recording.
     *
     * @return {Promise<void>} A promise that resolves once the event handlers are initialized.
     */
    async _initHandlers() {
        if (this.prepared) {
            await this._cleanHandlers();
        }

        this.recorder.addEventListener('dataavailable', this.onRecorderDataAvailable.bind(this));
        this.recorder.addEventListener('start', this.onRecorderStarted.bind(this));
        this.recorder.addEventListener('stop', this.onRecorderStopped.bind(this));
        this.recorder.addEventListener('pause', this.onRecorderPaused.bind(this));
        this.recorder.addEventListener('resume', this.onRecorderResumed.bind(this));
        this.recorder.addEventListener('error', this.onRecorderError.bind(this));
        //debug('>>>> Added Recorder Event Listeners');
    }

    /**
     * Cleans up and removes all event listeners attached to the recorder instance.
     *
     * @return {Promise<void>} A promise that resolves when all event listeners are successfully removed.
     */
    async _cleanHandlers() {
        if (this.recorder) {
            this.recorder.removeEventListener('dataavailable', this.onRecorderDataAvailable.bind(this));
            this.recorder.removeEventListener('start', this.onRecorderStarted.bind(this));
            this.recorder.removeEventListener('stop', this.onRecorderStopped.bind(this));
            this.recorder.removeEventListener('pause', this.onRecorderPaused.bind(this));
            this.recorder.removeEventListener('resume', this.onRecorderResumed.bind(this));
            this.recorder.removeEventListener('error', this.onRecorderError.bind(this));
        }
        //debug('<<<< Removed Recorder Event Listeners');
    }

    /**
     * Handles the event when the recorder starts.
     *
     * @return {Promise<void>} A promise that resolves when the recorder start logic is completed.
     */
    async onRecorderStarted() {

    }

    /**
     * Handles operations to perform when the recorder has stopped.
     * Sets the `closed` property to `true` to indicate the recorder is no longer active.
     *
     * @return {Promise<void>} A promise that resolves when the method execution is complete.
     */
    async onRecorderStopped() {

        this.closed = true;
    }

    /**
     * Handles the behavior when the recorder is paused.
     *
     * @return {Promise<void>} A promise that resolves when the handling of the pause event is complete.
     */
    async onRecorderPaused() {

    }

    /**
     * Handles the event when the recorder is resumed.
     * This method executes any necessary logic or updates required
     * upon the resumption of the recording process.
     *
     * @return {Promise<void>} A promise that resolves when the resume handling process is complete.
     */
    async onRecorderResumed() {

    }

    /**
     * Event handler for the error event triggered by the recorder in case any
     * error occurs.
     *
     * Adds the current recorder object to the detail event member and then
     * redispatches the event.
     *
     * @param {Event} evt
     */
    async onRecorderError(evt) {
        evt.detail.recorder = this;
        document.dispatchEvent(evt);
    }

    /**
     * Event handler for the dataavailable event that is triggered by the
     * recorder.
     *
     * Stores the stream data chunk included in the event, and notifies any
     * listeners about the new data coming from the stream.
     *
     * @param {Event} evt
     */
    async onRecorderDataAvailable(evt) {
        try {
            if (this.recorder && this.#stream && !this.closed && this.fileWriteHandle && !this.locked) {
                const blob = evt.data,
                    bufSize = blob.size,
                    buffer = new Uint8Array(await blob.arrayBuffer());
                try {
                    await this.fileWriteHandle.write(buffer, {at: this.filePos});
                    this.fileWriteHandle.flush && await this.fileWriteHandle.flush();
                } catch (err) {
                    debug('Could not write to file: %o', err);
                    return;
                }
                this.filePos += bufSize;
                //debug('Wrote %d bytes to file handler %s: %o', bufSize, this.fileName, this.fileHandle);
                await this.notify(this.streamType, evt.data);
            } else {
                debug('Received data-available event without a valid recorder or stream');
                debug('Recorder: %o', this.recorder);
                debug('Stream: %o', this.#stream);
            }

        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Event handler for the ended event triggered by the recorder.
     *
     * Closes the stream and files.
     *
     * @param {*} evt
     */
    async onShareEnded(evt) {
        debug('Recorder Ended Event: %o', evt);
        //console.info('Share ended for recorder : %s', this.recorder);
        await this.stop();
        if (this.stream) {
            let self = this;
            this.stream.getTracks().forEach(track => {
                self.stream.removeTrack(track);
            });
        }

        try {
            this.fileWriteHandle.flush && await this.fileWriteHandle.flush();
            await this.fileWriteHandle.close();
            debug('FileWriteHandle closed in onShareEnded event for file %s', this.fileName);
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Notifies listeners that new data arrived from the stream.
     *
     * @param {*} type
     * @param {*} blob
     */
    async notify(type, blob) {
        if (!this.#stream) {
            return;
        }

        const bufSize = blob.size,
            buffer = new Uint8Array(await blob.arrayBuffer());
        const ev = new CustomEvent(
            'recorder.data-available',
            {
                detail: {
                    uuid: this.#stream.id,
                    type: type,
                    data: buffer,
                    size: bufSize,
                    dataType: blob.type
                }
            }
        );
        document.dispatchEvent(ev);
    }
}

/**
 * Represents an audio recording utility that extends the functionality of a generic stream recorder.
 * This class provides methods for preparing and recording audio streams from the user's media devices.
 * It specializes in handling audio data streams and includes support for device-specific audio input.
 *
 * @extends StreamRecorder
 */
export class AudioRecorder extends StreamRecorder {
    audioDevId = null;

    /**
     * Creates an instance of the class and initializes audio stream settings.
     *
     * @param {string|null} audioDeviceId - The ID of the audio input device to use. If null, the default audio device will be used.
     * @return {void}
     */
    constructor(audioDeviceId = null) {
        super();

        this.streamType = 'audio';
        this.streamExtension = 'ogg';
        this.audioDevId = audioDeviceId;
        this.streamOptions = {
            audio: true,
            sampleRate: 44100,
        };

    }

    /**
     * @see {StreamRecorder.prepare}
     *
     * @return {Promise}
     */
    async prepare() {
        let self = this;

        return new Promise((resolve/*, reject*/) => {
            if (self.prepared) {
                resolve(true);
                return true;
            }

            if (self.audioDevId) {
                self.streamOptions.deviceId = { exact: self.audioDevId }
            }
            navigator.mediaDevices.getUserMedia(self.streamOptions).then((stream) => {
                let contextOptions = {
                        latencyHint: "interactive",
                    },
                    recorderOptions = {
                        mimeType: AUDIO_CODEC,
                    };

                // Firefox does not like having the sampleRate entry
                if (navigator.userAgent.indexOf('Firefox') < 0) {
                    contextOptions.sampleRate = 44100;
                    recorderOptions.sampleRate = 44100;
                }
                self.stream = stream;

                const ctx = new AudioContext(contextOptions);
                const src = ctx.createMediaStreamSource(self.stream);
                const audioDst = ctx.createMediaStreamDestination();
                const gain = new GainNode(ctx);
                gain.gain.value = 2;
                gain.gain.volume = 2;
                gain.gain.minVolume = 1;
                src.connect(gain);
                gain.connect(audioDst);

                self.recorder = new MediaRecorder(audioDst.stream, recorderOptions);
            }).then(() => {
                super.prepare().then(() => {
                    //debug('<=================> AudioRecorder ready: %o', self);
                    resolve(true);
                });
            });
        });
    }

    /**
     * Stops the current process and releases any associated media resources.
     *
     * This method overrides the `stop` method from the base class to perform additional cleanup of audio tracks
     * from the media stream, if any are present. It stops all audio tracks and removes them from the stream.
     *
     * @return {Promise<void>} A promise that resolves when the stop operation and cleanup are complete.
     */
    async stop() {
        await super.stop();

        if (this.stream) {
            let tracks = this.stream.getAudioTracks(),
                self = this;
            tracks.forEach((track) => {
                track.stop();
                self.stream.removeTrack(track);
            });
        }
    }

}

/**
 * ScreenRecorder is a class that extends StreamRecorder and is specifically used for recording screen
 * capture sessions with audio and video options.
 *
 * It handles the initialization and management of the screen capture streams, and provides functionality
 * for preparing, starting, stopping, and cleaning up after the screen recording process.
 */
export class ScreenRecorder extends StreamRecorder {
    videoDevId = null;

    /**
     * Initializes a new instance of the class, setting up the video stream configuration and optional video device ID.
     *
     * @param {string|null} videoDeviceId - The ID of the video device to use. If null, the default video device will be used.
     * @return {void}
     */
    constructor(videoDeviceId = null) {
        super();

        this.streamType = 'video';
        this.streamExtension = 'webm';
        this.videoDevId = videoDeviceId;
        this.streamOptions = {
            video: {
                displaySurface: ["monitor"],
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                frameRate: { ideal: 30 },
            },
            audio: true
        };
        if (videoDeviceId) {
            this.streamOptions.video.deviceId = { exact: videoDeviceId };
        }
    }

    /**
     * @see {StreamRecorder.prepare}
     *
     * @return {Promise}
     */
    async prepare() {
        let self = this;

        return new Promise((resolve, reject) => {
            if (self.prepared) {
                resolve(true);
                return true;
            }

            if (!self.stream && !self.recorder) {
                navigator.mediaDevices.getDisplayMedia(this.streamOptions).then((stream) => {
                    self.stream = stream;
                    //debug('Video Stream = %o', self.stream);

                    self.recorder = new MediaRecorder(
                        self.stream,
                        {
                            mimeType: VIDEO_CODEC,//;codecs=opus,vp8'
                        }
                    );
                }).then(() => {
                    super.prepare().then(() => {
                        resolve(true);
                    });
                }).catch((err) => {
                    console.error(err);
                    reject(err);
                });
            }

        });
    }

    /**
     * @see {ScreenRecorder._initHandlers}
     */
    async _initHandlers() {
        await super._initHandlers();

        if (this.stream) {
            let tracks = this.stream.getVideoTracks();
            let self = this;
            tracks.forEach((track) => {
                track.addEventListener('ended', self.onShareEnded.bind(self));
            });
        }

        //debug('>>>> Added ScreenRecorder Event Listeners');
    }

    /**
     * @see {ScreenRecorder._cleanHandlers}
     */
    async _cleanHandlers() {
        await super._cleanHandlers();
        /*
        if ((navigator.userAgent.indexOf('Firefox') > -1) && this.getDataTimer) {
            clearInterval(this.getDataTimer);
            this.getDataTimer = null;
        }*/

        if (this.stream) {
            let tracks = this.stream.getVideoTracks();
            let self = this;
            tracks.forEach((track) => {
                track.removeEventListener('ended', self.onShareEnded.bind(self));
            });
        }
        //debug('<<<< Removed ScreenRecorder Event Listeners');
    }

    /**
     * Stops the current process and associated media stream by halting all video tracks
     * and removing them from the stream, if applicable.
     *
     * @return {Promise<void>} A promise that resolves once the stop operation has completed.
     */
    async stop() {
        await super.stop();

        if (this.stream) {
            let tracks = this.stream.getVideoTracks(),
                self = this;
            tracks.forEach((track) => {
                track.stop();
                self.stream.removeTrack(track);
            });
        }
    }
}
